import { getFontWeight, getTextStyle } from "cms/theme/helpers";
import Styled from "styled-components";

export type ButtonVariant =
  // New
  | "primary"
  | "secondaryDark"
  | "secondaryLight"
  | "magic"
  | "ghostDark"
  | "ghostLight"
  | "outline"

  // Legacy
  | "dark"
  | "light"
  | "secondary"
  | "gradient"
  | "smokey";

export type TextColorVariant = "black" | "white";
export type ButtonSize = "small" | "medium" | "large";

export interface ContainerProps {
  $variant: ButtonVariant;
  $size?: ButtonSize;
  $fullWidth?: boolean;
  $hasIcon?: boolean;
}

export const Container = Styled.button<ContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border: 0;
  background: transparent;
  width: ${(props) => (props.$fullWidth ? "100%" : "auto")};
  cursor: pointer;
  letter-spacing: -0.1px;
  white-space: nowrap;
  transition: 300ms;
  ${(props) => getTextStyle(props.theme, "L3")}

  &:before, &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    transition: 300ms;
  }

  &:after {
    opacity: 0;
  }

  &:hover:before {
    opacity: 0;
  }

  &:hover:after {
    opacity: 1;
  }

  ${(props) =>
    props.$variant === "primary" &&
    `
    color: #FFFFFF;
    background-color: #1E90FF;
    
    &:before {
      background-color: #1E90FF;
      background: radial-gradient(42.88% 158.65% at 50% 100%, #61B1FF 0%, #1E90FF 100%);
      box-shadow: 0px 1px 1px 0px #69B5FF inset, 0px 2px 6px 0px rgba(3, 3, 3, 0.16);
    }

    &:after {
      background-color: #1277D9;
      background: radial-gradient(56.39% 208.65% at 50% 50%, #1E90FF 0%, #1277D9 100%);
      box-shadow: 0px 2px 12px 0px rgba(3, 3, 3, 0.24), 0px 1px 1px 0px #1E90FF inset;
    }
  `}

  ${(props) =>
    props.$variant === "secondaryDark" &&
    `
    color: #111111;
    background: #FFFFFF;
    box-shadow: 0px 2px 8px 0px #03030329;

    &:disabled {
      background: #212121;
    }

    &:hover {
      background: #FFFFFFA3;
      box-shadow: 0px 2px 12px 0px #0303033D;
    }
  `}

  ${(props) =>
    props.$variant === "secondaryLight" &&
    `
    color: #FFFFFF;
    background: #030303;
    box-shadow: 0px 1px 1px 0px #212121 inset, 0px 2px 8px 0px #03030329;

    &:hover {
      background: #2E2E2E;
      box-shadow: 0px 1px 1px 0px #212121 inset, 0px 2px 12px 0px #0303033D;
    }
  `}

  ${(props) =>
    props.$variant === "magic" &&
    `
    color: #FFFFFF;
    background-color: #A881FE;
    
    &:before {
      background-color: #A881FE;
      background: radial-gradient(59.46% 220% at 50% 100%, #A881FE 0%, #6419FF 100%);
      box-shadow: 0px 2px 6px 0px rgba(168, 129, 254, 0.16), 0px 1px 1px 0px #A881FE inset;
    }

    &:after {
      background-color: #6419FF;
      background: radial-gradient(72.97% 270% at 50% 50%, #A881FE 0%, #6419FF 100%);
      box-shadow: 0px 2px 12px 0px rgba(168, 129, 254, 0.64), 0px 1px 1px 0px #A881FE inset;
    }
  `}

  ${(props) =>
    props.$variant === "ghostDark" &&
    `
    color: #FFFFFF;

    &:hover {
      background: #7F7F7F3D;
    }
  `}

  ${(props) =>
    props.$variant === "ghostLight" &&
    `
    color: #030303;

    &:hover {
      background: #7F7F7F14;
    }
  `}

  ${(props) =>
    props.$variant === "outline" &&
    `
    color: #111111;
    background: transparent;
    border: 1px solid #E8E8E8;
    
    &:hover {
      border: 1px solid #212121;
    }
  `}

  ${(props) =>
    props.$size === "small" &&
    `
    height: 2.125rem;
    padding: 0 1rem;
    border-radius: 0.5rem;
    &:after, &:before {
      border-radius: 0.5rem;
    }
  `};

  ${(props) =>
    props.$size === "medium" &&
    `
    height: 2.125rem;
    padding: 0 1.25rem;
    border-radius: 0.5rem;
    &:after, &:before {
      border-radius: 0.5rem;
    }
  `};

  ${(props) =>
    props.$size === "large" &&
    `
    height: 2.625rem;
    padding: 0 1.5rem;
    border-radius: 0.625rem;
    &:after, &:before {
      border-radius: 0.625rem;
    }
  `};

  ${(props) => props.theme.breakpoints.up("medium")} {
    ${(props) =>
      props.$size === "small" &&
      `
      height: 2.375rem;
      padding: 0 1rem;
      border-radius: 0.5rem;
      &:after, &:before {
        border-radius: 0.5rem;
      }
    `};

    ${(props) =>
      props.$size === "medium" &&
      `
      height: 2.875rem;
      padding: 0 1.5rem;
      border-radius: 0.625rem;
      &:after, &:before {
        border-radius: 0.625rem;
      }
    `};

    ${(props) =>
      props.$size === "large" &&
      `
      height: 3rem;
      padding: 0 2rem;
      border-radius: 0.75rem;
      &:after, &:before {
        border-radius: 0.75rem;
      }

      ${props.$hasIcon && "padding: 0 1.5rem;"}
    `};
  }

  ${(props) => props.theme.breakpoints.up("large")} {
    ${(props) =>
      props.$size === "small" &&
      `
      height: 2.375rem;
      padding: 0 1rem;
      border-radius: 0.5rem;
      &:after, &:before {
        border-radius: 0.5rem;
      }
    `};

    ${(props) =>
      props.$size === "medium" &&
      `
      height: 2.875rem;
      padding: 0 2rem;
      border-radius: 0.625rem;
      &:after, &:before {
        border-radius: 0.625rem;
      }
      ${props.$variant === "magic" && "padding: 0 1.5rem;"}
    `};

    ${(props) =>
      props.$size === "large" &&
      `
      height: 3rem;
      padding: 0 2.5rem;
      border-radius: 0.75rem;
      &:after, &:before {
        border-radius: 0.75rem;
      }
      ${props.$hasIcon && "padding: 0 1.5rem;"}
    `};
  }

  &:disabled {
    color: ${(props) => props.theme.colors.tokens.text.textDisabled};
    background: ${(props) =>
      props.theme.mode === "light"
        ? props.theme.colors.neutrals.gray200
        : props.theme.colors.neutrals.gray800};
    pointer-events: none;
    cursor: not-allowed;
    box-shadow: none;
  }

  ${(props) =>
    (props.$variant === "ghostDark" || props.$variant === "ghostLight") &&
    `
    &:disabled {
      background: transparent;
    }
  `}
`;

export const Content = Styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  z-index: 1;
`;

export const IconImageContainer = Styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;

  & > img {
    width: 100%;
    height: 100%;
  }
`;
